<template>
  <div :skill="skill" :class="skillStateClassObject">
    <section class="mdc-card__supporting-text pl-section">
      <section class="mdc-card__primary" style="width: 100%;">
        <h3 class="mdc-card__title mdc-card__title--large" style="display: inline-block;" ><strong>{{ skill.skill_name }}</strong></h3>

        <div style="float: right;">
          <transition name="fade" mode="out-in">

            <!-- Actions to include a statement -->
            <template v-if="locked">
              <span
                key="add"
                class='label label-danger lock-label cursor-not-allowed'
                :aria-label="'Add ' + skill.skill_name + ' statement (Locked: Premium-Only'">
                <i class="fa fa-lock"/>
              </span>
            </template>

            <template v-else-if="isCurrentState([states.NOT_INCLUDED, states.LOADING])" >
              <span v-if="currentState === states.LOADING" class="loading-state">
                <i class="fa fa-circle-o-notch fa-spin"/>
                Adding...
              </span>
              <button
                v-else
                key="add"
                type="button"
                class="btn btn-success"
                style="display: inline-block;"
                :aria-label="'Add ' + skill.skill_name + ' statement'"
                @click="viewStatements">
                Add
              </button>
            </template>

            <!-- Actions to edit a saved statement -->
            <template v-else-if="currentState === states.SAVED">
              <TransitionGroup key="edit-remove">
                <button
                  key="edit"
                  type="button"
                  class="btn btn-primary"
                  :aria-label="'Edit ' + skill.skill_name + ' statement'"
                  @click="editSaved">Edit</button>
                <a
                  key="remove"
                  @click="() => { showRemoveConfirmationModal = true } "
                  class="btn btn-link text-danger"
                  :aria-label="'Remove ' + skill.skill_name + ' statement'"
                  href="javascript:void(0)">Remove</a>
              </TransitionGroup>
            </template>

            <!-- Actions if editing an existing statement -->
            <div v-else-if="isCurrentState([states.NEW_EDIT, states.SAVED_EDIT])">
              <button
                key="close"
                @click="closeEdit"
                type="button"
                class="btn btn-default"
                :aria-label="`Close ${skill.skill_name}`">Close</button>
            </div>
          </transition>

        </div>
      </section>
    </section>

    <template v-if="isCurrentState([states.NEW_EDIT, states.SAVED_EDIT])">
      <!-- Radio row for grade selection and present levels section-->
      <section class="mdc-card__supporting-text pl-section">
        <section class="pl-subsection">
          <div v-if="this.defaultStatements && Object.keys(this.defaultStatements).length > 2" style="width: 100%">
            <RadioRow
              :columns="levelsLayoutNumColumns"
              :label="useGradeSet || useLevelingSet ? 'top' : 'right'"
              :option-set="optionSet"
              :default-options="preselectedOptions"
              @selectedOptions="(options) => {multiSelect ? multiSelectedOptions = options : functionalGrade = options[0]}"
              :multi-select="multiSelect"
              :label-prefix="skill.skill_uuid"
            />
          </div>
        </section>

        <section class="pl-subsection">
          <CopyTrackWrapper
            type="presentlevel"
            :title="skill.skill_name"
            :uuid="skill.skill_uuid"
            :level="functionalGrade">
            <section v-if="!multiSelect">
              <CopyTrackWrapper
                type="presentlevel"
                :title="skill.skill_name"
                :uuid="skill.skill_uuid"
                :level="functionalGrade">
                <EditableWikiTextArea
                  class="gb-text-3 pl-editable-textarea"
                  :value="selectedStatement.text"
                  :reset-value="defaultText"
                  :resources="selectedStatement.resources"
                  @textReset="stopEditing"
                  @textEdited="stopEditing"
                  @activatedEditText="clearWikiTextFeedback"
                  :content-title="skill.skill_name"
                >
                  <template slot="wikiTextFeedback">
                    <div
                      class="text-small"
                      aria-live="polite">{{ wikiTextFeedback }}</div>
                  </template>
                </EditableWikiTextArea>

              </CopyTrackWrapper>
            </section>
            <section v-if="multiSelect">
              <div
                v-for="statement in multiSelectedStatements"
                :key="statement.uuid"
                class="multistatement">
                <CopyTrackWrapper
                  type="presentlevel"
                  :title="skill.skill_name"
                  :uuid="skill.skill_uuid"
                  :level="statement.selected_statement.level_label">
                  <h4 v-if="useEffectsTemplate ? multiSelectedStatements.length > 1 : true">{{ statement.selected_statement.level_label }}</h4>
                  <EditableWikiTextArea
                    class="gb-text-3 pl-editable-textarea"
                    :value="statement.statement_text"
                    :reset-value="populateDefaultStatement(statement.selected_statement.statement_text)"
                    :resources="statement.resources"
                    @textReset="stopEditingMultiSelect($event, statement.uuid, statement.selected_statement.level_label)"
                    @textEdited="stopEditingMultiSelect($event, statement.uuid, statement.selected_statement.level_label)"
                    @activatedEditText="clearWikiTextFeedback"
                    :content-title="`${skill.skill_name}: ${statement.selected_statement.level_label}`"
                  >
                    <template slot="wikiTextFeedback">
                      <div
                        class="text-small"
                        aria-live="polite">{{ wikiTextFeedback }}</div>
                    </template>
                  </EditableWikiTextArea>
                </CopyTrackWrapper>
              </div>
            </section>
          </CopyTrackWrapper>
        </section>
      </section>

      <!-- Standard information section -->
      <section class="mdc-card__supporting-text pl-section" v-if="Object.keys(selectedStatement.standards).length > 0">
        <div v-for="(standards, standardSet) in selectedStatement.standards" :key="standardSet">
          <h4 class="text-primary"><strong>{{ standardHeading(standardSet) }}</strong></h4>
          <CopyTrackWrapper
            v-for="s in standards"
            :key="s.standard_long_ref"
            class="pl-subsection text-muted"
            type="pl-standard"
            :title="s.standard_long_ref"
            :uuid="s.standard_long_ref">
            <PresentLevelsStandardView
              :set-code="s.standard_set_code"
              :long-ref="s.standard_long_ref"
              :short-ref="s.standard_short_ref"
              :standard-text="s.standard_text"
              :info-obj="s"/>
          </CopyTrackWrapper>
        </div>
      </section>

      <!-- Assessment resources section -->
      <section class="mdc-card__supporting-text pl-section" v-if="Object.keys(selectedStatement.resources).length > 0">
        <h4 class="text-primary"><strong>ASSESSMENT RESOURCES</strong></h4>
        <ul class="mdc-list">
          <template v-for="(res, key) in selectedStatement.resources">
            <ResourcePreviewCard
              :uuid="res.uuid"
              :title="res.title"
              :num_pages="res.num_pages"
              :description="res.description"
              :type="res.type"
              :screenshots="res.screenshots"
              :thumbnail_url="res.thumbnail_url"
              :grades="res.grades"
              :subjects="res.subjects"
              :download_links="res.download_links"
              :google_drive_links="res.google_drive_links"
              :key="key"/>
          </template>
        </ul>
      </section>
    </template>

    <!-- Saved Statement Text-->
    <template v-if="currentState === states.SAVED">
      <section class="mdc-card__supporting-text pl-section">
        <div class="gb-text-2">
          <CopyTrackWrapper
            type="presentlevel"
            :title="skill.skill_name"
            :uuid="skill.skill_uuid">
            <WikiText
              v-if="!multiSelect"
              :locked="blurPLWPermissions.blur"
              lock-mode="always_snippet_first_block"
              :plaintext="blurPLWPermissions.blur"
              class="fs-block"
              :text="selectedStatement.text"
              :resources="selectedStatement.resources" />
            <WikiText
              v-if="multiSelect"
              v-for="statement in multiSelectedStatements"
              :key="statement.uuid"
              :locked="blurPLWPermissions.blur"
              lock-mode="always_snippet_first_block"
              :plaintext="blurPLWPermissions.blur"
              class="fs-block"
              :text="statement.statement_text" />
          </CopyTrackWrapper>
        </div>
      </section>
    </template>

    <template v-if="currentState === states.NOT_INCLUDED">
      <div/>
    </template>
    <!-- </section> -->

    <!-- Remove confirmation modal -->
    <modal
      v-model="showRemoveConfirmationModal"
      title="Are you sure you want to remove this saved statement?"
      :footer="false">
      <div class="text-center">
        <button class="btn btn-lg btn-danger" @click="removeSaved">Remove</button>
        <button class="btn btn-lg btn-default" @click="() => { showRemoveConfirmationModal = false }">No</button>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  getGradesSet,
  populatePlaceholderText,
  getSkillTypes,
  getStatementsBySkill,
  postPresentLevelStudentStatement,
  deleteAllPresentLevelStudentStatementsForSkill,
  putPresentLevelStudentStatementSelectStatus,
  getEffectStatementsBySubjectAndGrade,
  returnGradeFromEnrolledGrade,
  UNLOCKED_IMPACT_UUIDS, DEFAULT_ALT_SKILL_LEVEL
} from '@/api/presentlevels'
import { blockPlwMixin } from '@/components/presentlevels/block_plw_mixin'
import { mapGetters, mapMutations } from 'vuex'
import { headingsForStandardSetCode } from '@/components/goals/common/standard_headings.js'

import PresentLevelsStandardView from '@/standards/PresentLevelsStandardView'
import ResourcePreviewCard from '@/components/ResourcePreviewCard'
import WikiText from '@/components/WikiText'
import EditableWikiTextArea from '@/components/EditableWikiTextArea'
import RadioRow from '@/components/presentlevels/RadioRow'
import CopyTrackWrapper from '@/components/CopyTrackWrapper'

const NO_FUNCTIONAL_GRADE = 'NoFunctionGrade'
const EFFECT_STATEMENT = 'Effect'

const States = Object.freeze({
  INIT: Symbol('Init'),
  NOT_INCLUDED: Symbol('NotIncluded'),
  NEW_EDIT: Symbol('NewEdit'),
  SAVED: Symbol('Saved'),
  SAVED_EDIT: Symbol('SavedEdit'),
  LOADING: Symbol('Loading'),
  SAVING: Symbol('Saving')
})

export default {
  name: 'SkillStatements',
  components: {
    WikiText,
    EditableWikiTextArea,
    PresentLevelsStandardView,
    RadioRow,
    CopyTrackWrapper,
    ResourcePreviewCard
  },
  mixins: [blockPlwMixin],
  props: {
    subject: {
      required: true,
      type: String
    },
    skill: {
      required: true,
      type: Object
    },
    skillType: {
      required: false,
      type: Symbol,
      default() {
        return getSkillTypes().SKILL
      },
      validator(val) {
        let skillTypes = getSkillTypes()
        let allowedValues = Object.values(skillTypes)

        return allowedValues.indexOf(val) > -1
      }
    },
    studentName: {
      required: false,
      type: String,
      default: undefined
    },
    studentGrade: {
      required: false,
      type: String,
      default: undefined
    },
    studentStatements: {
      required: false,
      type: Array,
      default() {
        return []
      }
    },
    presentLevelUuid: {
      required: false,
      type: String,
      default: undefined
    },
    multiSelect: {
      type: Boolean,
      default: false
    },
    locked: {
      type: Boolean,
      default: false
    },
    useEffectsTemplate: { // only for Clark County School District
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      states: States,
      currentState: States.INIT,

      // Enrolled grade of the student
      selectedGrade: this.studentGrade,

      // Default statements for the provided skills
      defaultStatements: null,

      // Previously saved statement for the provided skill
      savedStatement: this.studentStatements.find(stmnt => stmnt.is_selected) || null,
      savedStatements: this.studentStatements,

      // Statement being locally edited
      editedStatement: '',

      // Whether to view the saved statment or default statement when entering a statement edit
      viewSaved: false,

      showRemoveConfirmationModal: false,

      multiSelectedOptions: this.functionalGrade ? [this.functionalGrade] : [],

      wikiTextFeedback: '',

      unlockedImpactUuids: UNLOCKED_IMPACT_UUIDS
    }
  },
  created() {
    if (!this.savedStatements || this.savedStatements.length === 0) {
      this.currentState = States.NOT_INCLUDED
    } else {
      if (this.multiSelect) {
        this.multiSelectedOptions = this.savedStatements
          .filter(statement => {
            return statement.is_selected
          })
          .map(statement => {
            return statement.selected_statement.level_label
          })
        this.currentState = States.SAVED
      } else {
        if (!this.savedStatement) {
          this.currentState = States.NOT_INCLUDED
        } else {
          this.selectedGrade = this.savedStatement.selected_statement.level_label
          this.currentState = States.SAVED
        }
      }
    }
  },
  methods: {
    ...mapMutations('presentlevels', ['setSaving', 'setLastUpdate']),
    // Helper Methods
    isCurrentState(stateList) {
      return stateList.indexOf(this.currentState) > -1
    },
    standardHeading(standardSetCode) {
      return headingsForStandardSetCode(standardSetCode, {
        profileId: this.userContentProfileId
      })
    },
    populateDefaultStatement(statementText) {
      // Replace placeholder text
      let today = new Date()

      return populatePlaceholderText(statementText, {
        studentName: this.studentName,
        studentGrade: this.studentGrade,
        dateStr: [today.getMonth() + 1, '/', today.getDate(), '/', today.getFullYear()].join('')
      })
    },
    lockDefaultStatements(statements = []) {
      const sortedStatements = statements.slice().sort((a,b) => parseInt(a.level_order) - parseInt(b.level_order))
      if (sortedStatements[0].skill_type === 'skill') {
        // only the 3 highest levels available for skills:
        for (let i=(sortedStatements.length - 3); i >= 0; i--) {
          sortedStatements[i].locked = true
        }
      } else if (!this.unlockedImpactUuids.includes(sortedStatements[0].skill_uuid)) {
        // aside from specific impacts, all impact statements should be locked
        for (let i=0; i<statements.length; i++) {
          statements[i].locked = true
        }
      }
      return statements
    },
    findUnlockedStatement(statements) {
      let sortedDefaultStatements = Object.values(statements).sort((a,b) => {
        return parseInt(a.level_order) < parseInt(b.level_order) ? -1 : 1
      })
      return sortedDefaultStatements.find(s => !s.locked)
    },
    // STATE TRANSITIONS
    viewStatements() {
      this.$sendEvent(
        'Clicked Add Present Level Statement',
        {},
        {
          cu: this.skill.skill_uuid,
          ct: this.skill.skill_name
        }
      )

      if (this.shouldBlockPLW()) {
        return
      }

      if (this.isCurrentState([this.states.LOADING, this.states.NEW_EDIT])) {
        return
      }

      this.currentState = States.LOADING
      let promiseChain = new Promise(resolve => {
        resolve()
      })

      if (this.defaultStatements === null) {
        promiseChain = promiseChain.then(() => {
          return this.fetchStatements()
        })
      } else if (this.useEffectsTemplate && !!this.defaultStatements[EFFECT_STATEMENT]) {
        this.multiSelectedOptions = [EFFECT_STATEMENT]
      }

      if (!this.useGradeSet) {
        if (this.useEffectsTemplate) {
          promiseChain.then(() => {
            this.currentState = States.SAVED_EDIT
          })

        } else {
          promiseChain.then(() => {
            this.currentState = States.NEW_EDIT
            let defaultLevel =
              this.optionSet.filter(({ label }) => label === DEFAULT_ALT_SKILL_LEVEL)[0] ||
              this.optionSet[0]
            this.selectedGrade = defaultLevel.label
          })
        }
      } else {
        promiseChain = promiseChain.then(() => {
          this.currentState = States.NEW_EDIT
          return this.saveStudentStatement('', this.selectedStatement.text, this.functionalGrade)
        })

        promiseChain.then(() => {
          this.currentState = States.SAVED_EDIT
        })
      }
    },
    saveNewEdit() {
      this.saveStudentStatement('', this.selectedStatement.text, this.functionalGrade).then(() => {
        this.currentState = States.SAVED
      })
    },
    cancelNewEdit() {
      this.currentState = States.NOT_INCLUDED
    },
    editSaved() {
      this.$sendEvent('Clicked Edit Present Level Statement')

      if (this.shouldBlockPLW()) {
        return
      }

      let q = new Promise(resolve => {
        resolve()
      })

      if (this.defaultStatements === null) {
        q = q.then(() => {
          return this.fetchStatements()
        })
      }

      q.then(() => {
        this.currentState = States.SAVED_EDIT
      })
    },
    stopEditing(text) {
      this.viewSaved = true

      if (text) {
        this.editedStatement = text
      }
      if (this.currentState === States.SAVED_EDIT) {
        this.saveStudentStatement(
          this.savedStatement.uuid,
          this.selectedStatement.text,
          this.functionalGrade
        ).then(() => {
          this.wikiTextFeedback = 'Statement saved.'
        })
      }
    },
    stopEditingMultiSelect(text, uuid, label) {
      this.viewSaved = true
      if (this.currentState === States.SAVED_EDIT || this.currentState === States.NEW_EDIT) {
        this.currentState = States.SAVED_EDIT
        this.saveStudentStatement(uuid, text, label).then(() => {
          this.wikiTextFeedback = 'Statement saved.'
        })
      }
    },
    saveSavedEdit() {
      this.saveStudentStatement(
        this.savedStatement.uuid,
        this.selectedStatement.text,
        this.functionalGrade
      ).then(() => {
        this.currentState = States.SAVED
      })
    },
    closeEdit() {
      if (this.currentState === States.NEW_EDIT) {
        this.currentState = States.NOT_INCLUDED
      } else if (this.currentState === States.SAVED_EDIT) {
        if (this.multiSelect && this.multiSelectedOptions.length === 0) {
          this.currentState = States.NOT_INCLUDED
        } else {
          this.currentState = States.SAVED
          this.clearWikiTextFeedback()
        }
      }
      this.$sendEvent('Clicked Close Present Level Statement')
    },
    removeSaved() {
      this.deleteAllStudentStatements()
      this.multiSelectedOptions = []
      this.showRemoveConfirmationModal = false
      this.currentState = States.NOT_INCLUDED
    },

    // API METHODS
    fetchStatements() {
      if (this.useEffectsTemplate) {
        return getEffectStatementsBySubjectAndGrade(this.subject, returnGradeFromEnrolledGrade(this.studentGrade))
          .then(data => {
            this.defaultStatements = {}
            if (data.statements.length > 1) {
              data.statements.forEach(stmnt => {
                this.defaultStatements[stmnt.level_label] = stmnt
              })
            } else {
              data.statements.forEach(stmnt => {
                this.defaultStatements[EFFECT_STATEMENT] = stmnt
                this.multiSelectedOptions = [EFFECT_STATEMENT]
              })
            }
          })
          .catch(error => {
            console.error(error)
          })
      } else {
        return getStatementsBySkill(this.skill.skill_uuid, this.profileId, this.licenseSubType, this.subject)
          .then(data => {
            this.defaultStatements = {}
            data.statements.forEach(stmnt => {
              this.defaultStatements[stmnt.level_label] = stmnt
            })
          })
          .catch(error => {
            console.error(error)
          })
      }
    },
    saveStudentStatement(statementUuid, statementText, statementLabel) {
      this.setSaving(true)
      return postPresentLevelStudentStatement({
        uuid: this.presentLevelUuid,
        statementUuid,
        subject: this.subject,
        skillLevel: '',
        skillUuid: this.skill.skill_uuid,
        statementText,
        defaultStatement: this.defaultStatements[statementLabel],
        multiSelect: this.multiSelect,
        }
      )
        .then(data => {
          this.editedStatement = ''
          this.savedStatement = data.statement
          let ssIdx = this.savedStatements.findIndex(
            stmnt => stmnt.selected_statement.level_label === statementLabel
          )
          if (ssIdx >= 0) {
            this.$set(this.savedStatements, ssIdx, data.statement)
          } else {
            this.savedStatements.push(data.statement)
          }
          this.setSaving(false)
          this.setLastUpdate(new Date())
        })
        .catch(error => {
          console.error(error)
        })
    },
    deleteAllStudentStatements() {
      this.$sendEvent(
        'Clicked Delete Present Level Statement',
        {
          // Deprecated fields to be removed
          skill_uuid: this.skill.skill_uuid,
          skill_name: this.skill.skill_name
        },
        {
          cu: this.skill.skill_uuid,
          ct: this.skill.skill_name
        }
      )

      this.setSaving(true)
      return deleteAllPresentLevelStudentStatementsForSkill(
        this.presentLevelUuid,
        this.skill.skill_uuid
      )
        .then(() => {
          this.savedStatement = null
          this.savedStatements = []
          this.setSaving(false)
          this.setLastUpdate(new Date())
        })
        .catch(error => {
          console.error(error)
        })
    },
    clearWikiTextFeedback() {
      this.wikiTextFeedback = ''
    }
  },
  eventContext() {
    return {
      plw_statement_uuid: this.skill.skill_uuid,
      plw_statement_name: this.skill.skill_name,
      plw_statement_level: !this.multiSelect ? this.functionalGrade : null,
      plw_statement_type: this.skillTypeString,
      plw_statement_levels: this.multiSelectedOptions
    }
  },
  computed: {
    ...mapGetters('contentProfile', ['visibleStandardSets', 'profileId']),
    ...mapGetters('user', ['userContentProfileId','isPremium','licenseSubType']),
    skillStateClassObject() {
      return {
        'mdc-card': true,
        inactive:
          [States.INIT, States.NOT_INCLUDED, States.LOADING, States.SAVED].indexOf(
            this.currentState
          ) > -1,
        active: [States.NEW_EDIT, States.SAVED_EDIT, States.SAVING].indexOf(this.currentState) > -1,
        saved: [States.SAVED, States.SAVED_EDIT].indexOf(this.currentState) > -1
      }
    },
    preselectedOptions() {
      return this.multiSelect ? this.multiSelectedOptions : [this.functionalGrade]
    },
    optionSet() {
      let options = []

      if (!this.useGradeSet) {
        options = Object.keys(this.defaultStatements)
          .sort((skillA, skillB) => {
            return (
              parseInt(this.defaultStatements[skillA].level_order) -
              parseInt(this.defaultStatements[skillB].level_order)
            )
          })
          .map(key => {
            let stmnt = this.defaultStatements[key]
            return { label: stmnt.level_label, disabled: false }
          })
      } else {
        let gradesSet = getGradesSet()
        options = gradesSet
          .map((grade, idx) => {
            let gradeOption = {
              label: grade,
              disabled: true
            }

            if (this.defaultStatements[grade]) {
              gradeOption.disabled = false
            }

            return gradeOption
          })
          .filter(grade => {
            return !grade.disabled
          })
      }
      for (let option of options) {
        let defaultStatement = Object.values(this.defaultStatements).find(s => s.level_label === option.label)
        option.locked = defaultStatement.locked
      }
      return options
    },
    useGradeSet() {
      return (
        this.skillType !== getSkillTypes().IMPACT &&
        (this.subject === 'Math' || this.subject === 'Reading' || this.subject === 'Writing')
      )
    },
    useLevelingSet() {
      return this.optionSet.some(({ label }) => label === DEFAULT_ALT_SKILL_LEVEL)
    },
    levelsLayoutNumColumns() {
      if (this.useGradeSet) {
        return 14
      }
      if (this.useLevelingSet) {
        return 6
      }
      return 2
    },
    multiSelectedStatements() {
      return this.savedStatements.filter(
        statement =>
          this.multiSelectedOptions.indexOf(statement.selected_statement.level_label) > -1
      )
    },
    clickedStatementLevelEventInfo() {
      return {
        cu: this.skill.skill_uuid,
        ct: this.skill.skill_name
      }
    },
    functionalGrade: {
      get() {
        if (!this.useGradeSet) {
          switch (this.currentState) {
            case States.NEW_EDIT:
              return this.selectedGrade
            case States.SAVED_EDIT:
              return this.selectedGrade
            case States.SAVED:
              return this.savedStatement.selected_statement.level_label
            default:
              return NO_FUNCTIONAL_GRADE
          }
        } else if (this.defaultStatements !== null) {
          let gradesSet = getGradesSet()

          let maxOption = 0
          let minOption = gradesSet.length

          Object.keys(this.defaultStatements).forEach(k => {
            let idx = gradesSet.indexOf(k)
            if (k === 'Mastery') {
              return
            }

            if (idx > maxOption) {
              maxOption = idx
            }
            if (idx >= 0 && idx < minOption) {
              minOption = idx
            }
          })

          if (
            gradesSet.indexOf(this.selectedGrade) > maxOption &&
            this.selectedGrade !== 'Mastery'
          ) {
            return gradesSet[maxOption]
          } else if (gradesSet.indexOf(this.selectedGrade) < minOption) {
            return gradesSet[minOption]
          }

          if (this.defaultStatements[this.selectedGrade] === undefined) {
            if (
              this.defaultStatements['9-10th'] &&
              (this.selectedGrade === '9th' || this.selectedGrade === '10th')
            ) {
              return '9-10th'
            }

            if (
              this.defaultStatements['11-12th'] &&
              (this.selectedGrade === '11th' || this.selectedGrade === '12th')
            ) {
              return '11-12th'
            }
          }
        }

        return this.selectedGrade
      },
      set(grade) {
        this.selectedGrade = grade
        this.editedStatement = ''
        this.viewSaved = false
      }
    },
    selectedStatement() {
      let statement
      let statementText = ''
      let statementResources = {}
      let statementStandards = []

      let resourceReducer = resArr => {
        return (resArr || []).reduce((resMap, res) => {
          if (res.uuid && !res.error) {
            resMap[res.uuid] = res
          }

          return resMap
        }, {})
      }

      let returnedSavedStatement = this.savedStatements.find(
        stmnt => stmnt.selected_statement.level_label === this.functionalGrade
      )

      switch (this.currentState) {
        case States.INIT:
          statementText = 'Initializing.'
          break
        case States.LOADING:
          statementText = 'Loading.'
          break
        case States.SAVING:
          statementText = 'Saving'
          break
        case States.NOT_INCLUDED:
          statementText = 'No statement included.'
          break
        case States.SAVED:
          if (this.savedStatement === null) {
            statementText = 'You have not saved a statement for this skill!'
          } else {
            statementText = this.savedStatement.statement_text
            statementResources = resourceReducer(
              this.savedStatement.selected_statement.resource_docs
            )
          }
          break
        case States.SAVED_EDIT:
          if (this.editedStatement) {
            statementText = this.editedStatement

            if (this.savedStatement) {
              statementResources = resourceReducer(
                this.savedStatement.selected_statement.resource_docs
              )
            }
          } else if (this.savedStatement && this.viewSaved) {
            statementText = this.savedStatement.statement_text
            statementResources = resourceReducer(
              this.savedStatement.selected_statement.resource_docs
            )
          } else if (returnedSavedStatement) {
            statementText = returnedSavedStatement.statement_text
            statementResources = resourceReducer(
              returnedSavedStatement.selected_statement.resource_docs
            )
          } else if (this.defaultStatements === null) {
            statementText = 'Loading skill statements...'
          } else if (Object.keys(this.defaultStatements).length === 0) {
            statementText =
              'Sorry but we have no default statements for this skill, but you can still write your own statement here!'
          } else if (this.defaultStatements[this.functionalGrade]) {
            statement = this.defaultStatements[this.functionalGrade]

            statementText = this.populateDefaultStatement(statement.statement_text)
            statementResources = resourceReducer(statement.resource_docs)
          }
          break
        case States.NEW_EDIT:
          if (this.editedStatement) {
            statementText = this.editedStatement
            if (this.savedStatement) {
              statementResources = resourceReducer(
                this.savedStatement.selected_statement.resource_docs
              )
            }
          } else if (this.defaultStatements === null) {
            statementText = 'Loading skill statements...'
          } else if (Object.keys(this.defaultStatements).length === 0) {
            statementText =
              'Sorry but we have no default statements for this skill, but you can still write your own statement here!'
          } else if (this.defaultStatements[this.functionalGrade]) {
            statement = this.defaultStatement

            statementText = this.populateDefaultStatement(statement.statement_text)
            statementResources = resourceReducer(statement.resource_docs)
          } else {
            statementText = 'Choose a statement to include and edit.'
          }
          break
        default:
          statementText = 'Error! Unknown State!'
          break
      }

      for (const [uuid, res] of Object.entries(statementResources)) {
        res['$_source'] = 'present-levels'
        statementResources[uuid] = res
      }

      if (
        this.defaultStatements &&
        this.defaultStatements[this.functionalGrade] &&
        this.defaultStatements[this.functionalGrade].standard_docs
      ) {
        statementStandards = this.defaultStatements[this.functionalGrade].standard_docs
      }

      return {
        text: statementText,
        resources: statementResources,
        standards: statementStandards.reduce((standards, std) => {
          let keep =
            std.standard_long_ref &&
            std.standard_long_ref.trim() !== '' &&
            this.visibleStandardSets.includes(std.standard_set_code)

          if (keep) {
            if (!standards[std.standard_set_code]) {
              standards[std.standard_set_code] = []
            }
            standards[std.standard_set_code].push(std)
          }

          return standards
        }, {})
      }
    },
    defaultStatement() {
      let defaultStatement = this.defaultStatements
        ? this.defaultStatements[this.functionalGrade]
        : false

      if (defaultStatement && defaultStatement.locked) {
        defaultStatement = this.findUnlockedStatement(Object.values(this.defaultStatements))
      }

      return defaultStatement || {statement_text: '', resource_docs: []}
    },
    defaultText() {
      return this.defaultStatement
        ? this.populateDefaultStatement(this.defaultStatement.statement_text)
        : this.selectedStatement.statementText
    },
    skillTypeString() {
      if (this.skillType === getSkillTypes().SKILL) {
        return 'Skill'
      } else if (this.skillType === getSkillTypes().IMPACT) {
        return 'Impact'
      } else {
        return ''
      }
    }
  },
  watch: {
    functionalGrade(newFunctionalGrade) {
      if (this.multiSelect) {
        return
      }
      if (this.currentState === States.SAVED_EDIT) {
        this.$sendEvent('Clicked Statement Level', {}, this.clickedStatementLevelEventInfo)

        let savedStatement = this.savedStatements.find(
          stmnt => stmnt.selected_statement.level_label === newFunctionalGrade
        )

        this.saveStudentStatement(
          savedStatement ? savedStatement.uuid : '',
          this.selectedStatement.text,
          this.functionalGrade
        )
      } else if (this.currentState === States.NEW_EDIT) {
        this.saveStudentStatement('', this.selectedStatement.text, this.functionalGrade).then(
          () => {
            this.currentState = States.SAVED_EDIT
          }
        )
      }
    },
    multiSelect() {
      this.$sendEvent('Clicked Multiselect Checkbox', {multiselect: this.multiSelect}, this.clickedStatementLevelEventInfo)
    },
    async multiSelectedOptions(newOptions, oldOptions) {
      // save default statement if previously edited statement does not exist
      if (!this.defaultStatements) {
        await this.fetchStatements()
      }
      newOptions.forEach(option => {
        let ssIdx = -1
        if (this.savedStatements && this.savedStatements.length > 0) {
          ssIdx = this.savedStatements.findIndex(
            stmnt => stmnt.selected_statement.level_label === option
          )
        }
        if (ssIdx === -1 && this.defaultStatements[option]) {
          this.$sendEvent('Clicked Statement Level', {
            multiselect: this.multiSelect,
            plw_statement_level: newOptions.find(opt => !oldOptions.includes(opt)),
            plw_statement_levels: newOptions
          }, this.clickedStatementLevelEventInfo)
          this.saveStudentStatement(
            '',
            this.populateDefaultStatement(this.defaultStatements[option].statement_text),
            option
          )
        } else {
          this.saveStudentStatement(
            this.savedStatements[ssIdx].uuid,
            this.savedStatements[ssIdx].statement_text,
            option
          )
        }
      })
      let deselectedOptions = oldOptions.filter(option => !newOptions.includes(option))
      if (deselectedOptions.length > 0) {
        deselectedOptions.forEach(option => {
          let statement = this.savedStatements.find(
            stmnt => stmnt.selected_statement.level_label === option
          )
          if (statement && statement.uuid) {
            putPresentLevelStudentStatementSelectStatus(
              this.presentLevelUuid,
              statement.uuid,
              false
            )
          }
        })
      }
      if (this.currentState === States.NEW_EDIT) {
        this.currentState = States.SAVED_EDIT
      }
    }
  }
}
</script>

<style scoped lang="scss">
@import '@material/card/mdc-card';
@import '@material/list/mdc-list';

$preferred_font: 'Helvetica Neue', Helvetica, Arial, sans-serif;

/* fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

section.pl-section {
  padding-top: 0.75em;
  padding-bottom: 0.75em;

  section.pl-subsection,
  div.pl-subsection,
  h4 {
    padding-top: 0.5em;
    padding-bottom: 0.5em;
    margin: 0em;
  }
}

.mdc-card__supporting-text {
  font-family: $preferred_font;
}

.mdc-card {
  margin-bottom: 2em;
  padding-top: 0.75em;
  padding-bottom: 0.75em;

  .mdc-card__primary {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0em;

    .mdc-card__title--large {
      padding-top: 0em;
      font-family: $preferred_font;
    }
  }
}

.mdc-card__title mdc-card__title--large {
  padding-top: 0em;
}

.mdc-list {
  padding: 0em;
  font-family: $preferred_font;
}

.mdc-card.active {
  background: #d5eefa;

  .mdc-card__supporting-text {
    overflow: auto;

    .pl-textarea {
      padding: 15px;
      background: #ffc;
      color: #000;
    }
  }
}

.mdc-card.saved {
  .mdc-card__supporting-text {
    .gb-text-2 {
      color: #275D8B;
      font-style: italic;
      font-weight: normal;
      line-height: 28px;
    }
  }
}

.pl-textarea {
  resize: none;
}

a.text-danger {
  color: #a94442;
}
.multistatement:not(.multistatement:last-child){
  margin-bottom: 16px;
}
.btn-danger {
  margin-right: 2px;
  border: none;
  background-color: var(--red-default);
  &:hover, &:focus {
    background-color: var(--red-button-hover);
  }

  &:focus {
    outline: solid 2px var(--red-button-hover);
    outline-offset: 2px;
  }
}
.btn-success{
  background-color: #218739;
  border-color: #1D7732;
}
.loading-state {
  display: flex;
  align-items: center;
  color: var(--gray-default);
}
.fa-circle-o-notch {
  margin-right: 4px;
}
.text-small {
  font-size: 15px;
  line-height: 20px;
  padding-left: 5px;
  display: inline-block;
  vertical-align: top;
  color: #000;
}
.lock-label {
  font-size: 1.75rem;
  padding: 0.05em 0.25em;
}
</style>

<style lang="scss">
.pl-editable-textarea .rendered-text-container {
  background: white;
  padding: 0.5em !important;
}
</style>
